import {
  CardFragment,
  ElementInput,
  ElementPictureInput,
  ElementStickerInput,
  PanelInput,
  UpdateCardInput,
} from 'src/graphql/generated/graphql'
import {
  EditorElement,
  EditorFullBleed,
  EditorImage,
  EditorPanel,
  EditorSticker,
} from './EditorCard'
import paragraphInputs from './text/paragraphInputs'
import omit from 'lodash/omit'

const createElementPictureInput = ({
  image,
  position,
  scale,
  filter,
}: EditorImage): ElementPictureInput =>
  !image
    ? { image: null }
    : {
        image: image.id,
        position,
        scale,
        filter: filter ? filter.name : null,
      }

const createElementStickerInput = ({
  sticker,
  position,
  scale,
}: EditorSticker): ElementStickerInput =>
  !sticker
    ? { sticker: null }
    : {
        sticker: sticker.id,
        position,
        scale,
      }

export const createBackPanelPanelInput = (
  backFullBleedPanel: EditorFullBleed,
): PanelInput => {
  const panel = backFullBleedPanel.panels[0]
  const backgroundElement = backFullBleedPanel.backgroundElement
  return {
    backgroundColorObject: panel.backgroundColor
      ? {
          blue: panel.backgroundColor.blue,
          green: panel.backgroundColor.green,
          red: panel.backgroundColor.red,
        }
      : undefined,
    location: 0,
    isFullBleed: false,
    elements: backgroundElement
      ? [
          createElemenInput(backgroundElement),
          ...panel.elements.map(createElemenInput),
        ]
      : panel.elements.map(createElemenInput),
  }
}

const createElemenInput = (element: EditorElement): ElementInput => {
  return {
    sticker: element.sticker
      ? createElementStickerInput(element.sticker)
      : null,
    image: element.image ? createElementPictureInput(element.image) : null,
    text: element.text ? { paragraphs: paragraphInputs(element.text) } : null,
    draggable: element.draggable,
    height: element.height,
    width: element.width,
    locked: element.locked,
    rotation: element.rotation,
    x: element.x,
    y: element.y,
    z: element.z,
  }
}

const legacyFullBleedBackgroundPanels = (
  fullBleed: EditorFullBleed,
  fullBleedLocation: number,
): PanelInput[] =>
  fullBleed.backgroundElement
    ? [
        {
          location: fullBleedLocation,
          isFullBleed: true,
          backgroundColor: null,
          elements: fullBleed.backgroundElement
            ? [createElemenInput(fullBleed.backgroundElement)]
            : [],
        },
      ]
    : []

const fullBleedBackgroundPanels = (
  fullBleed: EditorFullBleed,
  fullBleedLocation: number,
): PanelInput[] =>
  fullBleed.name === 'FBBackground'
    ? [
        {
          location: fullBleedLocation,
          isFullBleed: true,
          backgroundColor: null,
          elements: fullBleed.panels[0].elements[0]
            ? [createElemenInput(fullBleed.panels[0].elements[0])]
            : [],
        },
      ]
    : []

export const createPanelInput = (panel: EditorPanel): PanelInput => ({
  backgroundColor:
    panel.backgroundColor && omit(panel.backgroundColor, '__typename'),
  elements: panel.elements.map(createElemenInput),
  isFullBleed: false,
  location: 0,
})

const convertLegacyPanelsToPanelInputList = (
  fullBleeds: EditorFullBleed[],
): PanelInput[] =>
  fullBleeds
    .flatMap((fullBleed, fullBleedLocation) => [
      ...legacyFullBleedBackgroundPanels(fullBleed, fullBleedLocation),
      ...fullBleed.panels.map(panel => ({
        location: undefined,
        isFullBleed: false,
        backgroundColor: panel.backgroundColor,
        elements: panel.elements.map(createElemenInput),
      })),
    ])
    .reduce(
      (
        { location, panels }: { location: number; panels: PanelInput[] },
        panel,
      ) => ({
        location: panel.location === undefined ? location + 1 : location,
        panels: [
          ...panels,
          {
            ...panel,
            backgroundColor: undefined,
            backgroundColorObject: panel.backgroundColor
              ? omit(panel.backgroundColor, '__typename')
              : undefined,
            location: panel.location === undefined ? location : panel.location,
          },
        ],
      }),
      { location: 0, panels: Array<PanelInput>() },
    ).panels

const createPanelInputList = (fullBleeds: EditorFullBleed[]): PanelInput[] =>
  fullBleeds
    .flatMap((fullBleed, fullBleedLocation) => [
      ...fullBleedBackgroundPanels(fullBleed, fullBleedLocation),
      ...fullBleed.panels
        .filter(fb => fb.name !== 'FBBackground')
        .map(panel => ({
          location: undefined,
          isFullBleed: false,
          backgroundColor: panel.backgroundColor,
          elements: panel.elements.map(createElemenInput),
        })),
    ])
    .reduce(
      (
        { location, panels }: { location: number; panels: PanelInput[] },
        panel,
      ) => ({
        location: panel.location === undefined ? location + 1 : location,
        panels: [
          ...panels,
          {
            ...panel,
            backgroundColor: undefined,
            backgroundColorObject: panel.backgroundColor
              ? omit(panel.backgroundColor, '__typename')
              : undefined,
            location: panel.location === undefined ? location : panel.location,
          },
        ],
      }),
      { location: 0, panels: Array<PanelInput>() },
    ).panels

const prepareFullBleed = (fullBleeds: EditorFullBleed[]): EditorFullBleed[] => {
  const fbBackIndex = fullBleeds.findIndex(fb =>
    fb.panels.some(panel => panel.name === 'FBBackground'),
  )
  if (fbBackIndex !== -1) {
    const fbBackElement = fullBleeds.splice(fbBackIndex, 1)[0]
    fullBleeds.splice(1, 0, fbBackElement)
    return fullBleeds
  }
  return fullBleeds
}

const isFullBleedHorizontal = (fullBleed?: EditorFullBleed) =>
  fullBleed ? fullBleed.width > fullBleed.height : undefined

export const createUpdateCardInput = (
  card: CardFragment,
  fullBleeds: EditorFullBleed[],
  recipient: string | null = null,
): UpdateCardInput => ({
  id: card.id,
  panels: card.isNewEditorCard
    ? createPanelInputList(prepareFullBleed(fullBleeds))
    : convertLegacyPanelsToPanelInputList(fullBleeds),
  recipient,
  isBackPanelHorizontal: card.isHorizontal
    ? isFullBleedHorizontal(
        fullBleeds.find(fullBleed => fullBleed.name === 'Back'),
      )
    : undefined,
})
